import {Alert, Badge, Group, Paper, Select, SimpleGrid, Stack, Text, TextInput, Title} from "@mantine/core";
import {DataTable, type DataTableSortStatus} from "mantine-datatable";
import {useMemo} from "react";
import {useNavigate} from "react-router";
import Page from "../../components/Page/Page.tsx";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import useDataTableSessionState from "../../hooks/useDataTableSessionState.ts";
import useVendorsData from "./useVendorsData";
import {getCategoryOrder, getWareCategoryColor, getWareCategoryLabel} from "./utils";
import type {WareAggregateRow} from "./types";
import useScreenSize from "../../components/useScreenSize.tsx";

const integerFormat = new Intl.NumberFormat();
const priceFormat = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});

const formatPrice = (value: number | null): string => {
    if (value === null) return '-';
    return priceFormat.format(value);
};

const sortRows = <T extends Record<string, unknown>>(rows: T[], sortStatus: DataTableSortStatus<T>): T[] => {
    const accessor = sortStatus.columnAccessor as string;
    const sorted = [...rows].sort((a, b) => {
        const left = a[accessor];
        const right = b[accessor];

        if (typeof left === 'number' || typeof right === 'number') {
            return (typeof left === 'number' ? left : -Infinity) - (typeof right === 'number' ? right : -Infinity);
        }

        return String(left ?? '').localeCompare(String(right ?? ''));
    });

    return sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
};

const renderWareTypeBadges = (row: WareAggregateRow) => {
    const {isMedium} = useScreenSize();
    return (
      <Group gap={6}>
          <Badge color={getWareCategoryColor(row.category)} variant="light" >
              {getWareCategoryLabel(row.category)}
              {isMedium && row.subTypes.length > 0 ? ` / ${row.subTypes[0]}` : ''}
          </Badge>
          {row.subTypes.length > 1 ? (
            <Badge color="gray" variant="outline">+{row.subTypes.length - 1} subtype{row.subTypes.length > 2 ? 's' : ''}</Badge>
          ) : null}
      </Group>
    )
};

export default function Wares() {
    const navigate = useNavigate();
    const {wareRows, stats, isPending, isError, error} = useVendorsData();
    const {sortStatus: wareSortStatus, setSortStatus: setWareSortStatus, filters, setFilter} = useDataTableSessionState<
        DataTableSortStatus<WareAggregateRow>,
        {wareSearch: string; typeSubtypeFilter: string}
    >({
        storageKey: 'vendors:wares:list',
        initialSortStatus: {
            columnAccessor: 'name',
            direction: 'asc',
        },
        initialFilters: {
            wareSearch: '',
            typeSubtypeFilter: 'all',
        },
    });
    const wareSearch = typeof filters.wareSearch === 'string' ? filters.wareSearch : '';
    const typeSubtypeFilter = typeof filters.typeSubtypeFilter === 'string' ? filters.typeSubtypeFilter : 'all';

    const typeSubtypeOptions = useMemo(() => {
        const grouped = getCategoryOrder().map((category) => {
            const subtypeSet = new Set<string>();
            for (const row of wareRows) {
                if (row.category !== category) continue;
                for (const subType of row.subTypes) {
                    if (subType.trim() !== '') {
                        subtypeSet.add(subType);
                    }
                }
            }

            const subtypeItems = Array.from(subtypeSet)
                .sort((a, b) => a.localeCompare(b))
                .map((subType) => ({
                    value: `subtype:${category}:${subType}`,
                    label: subType,
                }));

            return {
                group: getWareCategoryLabel(category),
                items: [
                    {value: `type:${category}`, label: `All ${getWareCategoryLabel(category)}`},
                    ...subtypeItems,
                ],
            };
        });

        return [
            {value: 'all', label: 'All types and subtypes'},
            ...grouped,
        ];
    }, [wareRows]);

    const filteredWares = useMemo(() => {
        const search = wareSearch.trim().toLowerCase();

        let selectedType: string | null = null;
        let selectedSubType: string | null = null;
        if (typeSubtypeFilter.startsWith('type:')) {
            selectedType = typeSubtypeFilter.slice('type:'.length);
        } else if (typeSubtypeFilter.startsWith('subtype:')) {
            const [, type, ...subTypeParts] = typeSubtypeFilter.split(':');
            selectedType = type ?? null;
            selectedSubType = subTypeParts.join(':') || null;
        }

        if (!search) {
            return wareRows.filter((row) => {
                if (selectedType && row.category !== selectedType) return false;
                if (selectedSubType && !row.subTypes.includes(selectedSubType)) return false;
                return true;
            });
        }
        return wareRows.filter((row) => {
            if (selectedType && row.category !== selectedType) return false;
            if (selectedSubType && !row.subTypes.includes(selectedSubType)) return false;

            return row.name.toLowerCase().includes(search)
                || row.subTypes.some(type => type.toLowerCase().includes(search))
                || row.category.toLowerCase().includes(search);
        });
    }, [wareRows, wareSearch, typeSubtypeFilter]);

    // @ts-expect-error weirdness with Record extends
    const sortedWares: WareAggregateRow[] = useMemo(() => sortRows(filteredWares, wareSortStatus), [filteredWares, wareSortStatus]);

    if (isPending) {
        return <LoaderScreen message="Loading wares" />;
    }

    return (
        <Page>
            <title>Wares</title>

            <Stack gap="md">
                <div>
                    <Title order={1}>Wares</Title>
                </div>

                {isError ? (
                    <Alert color="red" title="Unable to load wares">
                        {error instanceof Error ? error.message : 'Request failed'}
                    </Alert>
                ) : null}

                <SimpleGrid cols={{base: 2, md: 4}} spacing="md">
                    <Paper withBorder p="md" radius="md">
                        <Text c="dimmed" size="sm">Distinct Wares</Text>
                        <Title order={3}>{integerFormat.format(stats.distinctWares)}</Title>
                    </Paper>
                    <Paper withBorder p="md" radius="md">
                        <Text c="dimmed" size="sm">Ware Listings</Text>
                        <Title order={3}>{integerFormat.format(stats.totalListings)}</Title>
                    </Paper>
                    <Paper withBorder p="md" radius="md">
                        <Text c="dimmed" size="sm">Vendors</Text>
                        <Title order={3}>{integerFormat.format(stats.vendorCount)}</Title>
                    </Paper>
                    <Paper withBorder p="md" radius="md">
                        <Text c="dimmed" size="sm">Type Coverage</Text>
                        <Group gap={6} mt="xs">
                            {getCategoryOrder().map((category) => (
                                <Badge key={category} color={getWareCategoryColor(category)} variant="light">
                                    {getWareCategoryLabel(category)} {stats.categoryCounts[category]}
                                </Badge>
                            ))}
                        </Group>
                    </Paper>
                </SimpleGrid>

                <Paper withBorder p="md" radius="md">
                    <Group align="end">
                        <TextInput
                            label="Search wares"
                            placeholder="Ware name or type"
                            value={wareSearch}
                            onChange={(e) => setFilter('wareSearch', e.currentTarget.value)}
                            style={{flex: 1, minWidth: 240}}
                        />
                        <Select
                            label="Type / Subtype"
                            data={typeSubtypeOptions}
                            value={typeSubtypeFilter}
                            onChange={(value) => setFilter('typeSubtypeFilter', value ?? 'all')}
                            placeholder="Filter by type"
                            searchable
                            clearable={true}
                            style={{minWidth: 280}}
                        />
                    </Group>
                </Paper>

                <Paper withBorder p="md" radius="md">
                    <Group justify="space-between" mb="xs">
                        <Text size="sm" c="dimmed">
                            {integerFormat.format(sortedWares.length)} wares
                        </Text>
                        <Text size="sm" c="dimmed">Click a ware to view all vendors</Text>
                    </Group>

                    <DataTable<WareAggregateRow>
                        idAccessor="id"
                        records={sortedWares}
                        onRowClick={({record}) => {
                            navigate(`/wares/${record.category}/${record.wareKey}`);
                        }}
                        sortStatus={wareSortStatus}
                        onSortStatusChange={setWareSortStatus}
                        columns={[
                            {accessor: 'name', title: 'Ware', sortable: true},
                            {
                                accessor: 'category',
                                title: 'Type',
                                sortable: true,
                                render: renderWareTypeBadges,
                            },
                            {
                                accessor: 'vendorCount',
                                title: 'Vendors',
                                sortable: true,
                                render: (row) => integerFormat.format(row.vendorCount),
                            },
                            {
                                accessor: 'totalCount',
                                title: 'Total available',
                                sortable: true,
                                render: row => <div style={{textAlign: 'right'}}>{integerFormat.format(row.totalCount)}</div>,
                            },
                            {
                                accessor: 'minPrice',
                                title: 'Min',
                                sortable: true,
                                render: (row) => <div style={{textAlign: 'right'}}>{formatPrice(row.minPrice)}</div>,
                            },
                            {
                                accessor: 'avgPrice',
                                title: 'Avg',
                                sortable: true,
                                render: (row) => <div style={{textAlign: 'right'}}>{formatPrice(row.avgPrice)}</div>,
                            },
                            {
                                accessor: 'maxPrice',
                                title: 'Max',
                                sortable: true,
                                render: (row) => <div style={{textAlign: 'right'}}>{formatPrice(row.maxPrice)}</div>,
                            },
                        ]}
                    />
                </Paper>
            </Stack>
        </Page>
    );
}
